import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ListChecks, Plus, Trash, FloppyDisk, Clock } from "@phosphor-icons/react";
import { api } from "../lib/api";

// "HH:MM" (24h) → minutes since midnight; blank/invalid sort last.
function timeKey(t) {
  if (!t) return 24 * 60 + 1;
  const [h, m] = String(t).split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return 24 * 60 + 1;
  return h * 60 + m;
}
// "14:30" → "2:30 PM" for friendly display.
function fmt12(t) {
  if (!t) return "—";
  const [h, m] = String(t).split(":").map(Number);
  if (Number.isNaN(h)) return t;
  const ap = h < 12 ? "AM" : "PM";
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
}

let _seq = 0;
const nextKey = () => `tmp-${++_seq}`;

export default function ChecklistMaster() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [err, setErr] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/api/checklist/template");
      setItems((data.items || []).map((it) => ({ ...it, _k: it.id || nextKey() })));
      setDirty(false);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function setRow(k, patch) { setItems((a) => a.map((it) => (it._k === k ? { ...it, ...patch } : it))); setDirty(true); }
  function addRow() { setItems((a) => [...a, { id: "", time: "", task: "", _k: nextKey() }]); setDirty(true); }
  function removeRow(k) { setItems((a) => a.filter((it) => it._k !== k)); setDirty(true); }

  async function save() {
    const clean = items
      .map((it) => ({ id: it.id || undefined, time: (it.time || "").trim(), task: (it.task || "").trim() }))
      .filter((it) => it.task);
    if (clean.length === 0) { setErr("Add at least one checklist item."); return; }
    setSaving(true); setErr(null);
    try {
      const { data } = await api.put("/api/checklist/template", { items: clean });
      setItems((data.items || []).map((it) => ({ ...it, _k: it.id || nextKey() })));
      setDirty(false);
    } catch {
      setErr("Could not save. Please try again.");
    } finally { setSaving(false); }
  }

  // Always render earliest time first (untimed rows fall to the bottom).
  const sorted = [...items].sort((a, b) => timeKey(a.time) - timeKey(b.time));

  return (
    <div className="font-sans pb-4" data-testid="checklist-master">
      {/* Frozen header */}
      <div className="sticky top-0 z-20 -mx-4 border-b border-white/5 bg-[#0a0a0b]/95 px-4 pb-3 backdrop-blur-xl" style={{ paddingTop: "max(0.25rem, env(safe-area-inset-top))" }}>
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white/70 hover:text-white">
          <ArrowLeft size={16} weight="bold" /> Back
        </button>
        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-neon-cyan/15 text-neon-cyan"><ListChecks size={18} weight="duotone" /></div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-white/50">Master</p>
              <h1 className="font-display text-2xl font-extrabold text-white">Daily Checklist</h1>
            </div>
          </div>
          <button onClick={save} disabled={saving || !dirty} data-testid="checklist-master-save"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-neon-cyan px-4 py-2 text-[12px] font-bold text-black shadow-glow-cyan transition-all disabled:bg-white/10 disabled:text-white/40 disabled:shadow-none">
            <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
        <p className="mt-2 text-[11px] text-white/45">Items are ordered by time automatically. The time is also used to alert staff if a task is still open 30 min after its scheduled time.</p>
      </div>

      {loading ? (
        <div className="card mt-3 p-8 text-center text-sm text-white/55">Loading…</div>
      ) : (
        <div className="mt-3 space-y-2">
          {sorted.map((it) => (
            <div key={it._k} className="card p-3" data-testid={`master-item-${it._k}`}>
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/[0.03] px-2.5 py-2 focus-within:border-neon-cyan">
                  <Clock size={14} weight="bold" className="text-neon-cyan" />
                  <input type="time" className="bg-transparent text-[13px] text-white outline-none [color-scheme:dark]"
                    value={it.time || ""} onChange={(e) => setRow(it._k, { time: e.target.value })} data-testid={`master-time-${it._k}`} />
                </label>
                <span className="text-[11px] font-semibold text-white/45">{fmt12(it.time)}</span>
                <button onClick={() => removeRow(it._k)} data-testid={`master-remove-${it._k}`}
                  className="ml-auto shrink-0 rounded-full bg-white/5 p-2 text-white/50 hover:text-neon-pink"><Trash size={14} weight="bold" /></button>
              </div>
              <input className="field mt-2 text-[13px]" placeholder="Task description" value={it.task}
                onChange={(e) => setRow(it._k, { task: e.target.value })} data-testid={`master-task-${it._k}`} />
            </div>
          ))}

          <button onClick={addRow} data-testid="master-add-item"
            className="card flex w-full items-center justify-center gap-2 border-dashed border-white/15 p-3 text-[13px] font-bold text-neon-cyan hover:bg-white/[0.03]">
            <Plus size={16} weight="bold" /> Add checklist item
          </button>

          {err && <p className="rounded-xl border border-neon-pink/30 bg-neon-pink/10 px-3 py-2 text-sm text-neon-pink">{err}</p>}
        </div>
      )}
    </div>
  );
}
